import React from "react";
import { useClientContext } from "../contexts/client";
import { ReportDownloadButton } from "custom_components/ReportDownloadButton";
import { MenuItem, TextField, FormLabel, Typography } from "@mui/material";
import { usePermissions, useDataProvider } from "react-admin";
import Paper from "@mui/material/Paper";
import moment from "moment";
import LinearProgress from "@mui/material/LinearProgress";
import loadable from '@loadable/component'
import PrintFrame from "./iFrame";


const Dashboard = loadable(() => import("./Dashboard"));

const PresentationDashboard = (props) => {
  const { permissions } = usePermissions();
  const { state: clientContext } = useClientContext();
  const [selectedReport, setSelectedReport] = React.useState("");
  const [resourceName, setResourceName] = React.useState(null);
  const [aggregateRange, setAggregateRange] = React.useState("");
  const [aggregateRanges, setAggregateRanges] = React.useState([]);
  const [lastRunResults, setLastRunResults] = React.useState({});
  const [availableDashboards, setAvailableDashboards] = React.useState([]);
  const [dashboardConfig, setDashboardConfig] = React.useState({});
  const [loadingStatus, setLoadingStatus] = React.useState("loading");
  const [ hasData, setHasData ] = React.useState(false);
  const componentRef = React.useRef();

  const dataProvider = useDataProvider();

  const {
    record,
    client,
    deploy_target,
    filters,
    filter_by,
    filter_field
  } = props;

  const getAggregateRanges = React.useCallback(
    (resource_name) => {
      setAggregateRanges([]);

      return dataProvider
        .getList(`${client}_report_suites`, {
          filter: {
            "resource_name@eq": resource_name,
          },
          sort: { order: "desc", field: "table_name" },
          pagination: { page: 1, perPage: 500 },
        })
        .then((data) => {
          if (data && data.data && data.data.length) {
            setAggregateRanges(data.data[0].aggregate_ranges);
            setLastRunResults(data.data[0].result_of_last_run);
            setAggregateRange(data.data[0].aggregate_ranges[0]);
          }
        });
    },
    [client, dataProvider]
  );

  React.useEffect(() => {
    if (permissions && client && deploy_target) {
     
      dataProvider
        .getList(`${client}_dashboard_deployments`, {
          sort: { order: "asc", field: "position" },
          pagination: { page: 1, perPage: 100 },
          filter: {
            'deploy_target@ilike': deploy_target,
          },
        })
        .then((data) => {
          setLoadingStatus("loaded");
          
          if (data.data.length > 0) {
            setHasData(true);
          }

          let permissionFilteredData = data.data;
          if (permissions !== "site_admin") {
            permissionFilteredData = permissionFilteredData.filter(
              (r) => !r.is_draft
            );
          }

          setAvailableDashboards(permissionFilteredData);

          if (permissionFilteredData.length > 0) {
            getAggregateRanges(permissionFilteredData[0].resource_name);
            setResourceName(permissionFilteredData[0].resource_name);
            setDashboardConfig(permissionFilteredData[0].configuration);
            setSelectedReport(permissionFilteredData[0].title);
          }
        })
        .catch((e) => {
          // window.location.href = '#/'
          // logout()
        });
    }
  }, [permissions, client, deploy_target, record]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSelectedReport = React.useCallback(
    (e) => {
      const value = e.target.value;
      const _resourceName = availableDashboards.filter(
        (e) => e.title === value
      )[0].resource_name;

      setSelectedReport(value);
      setResourceName(_resourceName);
      setDashboardConfig(
        availableDashboards.filter((e) => e.title === value)[0].configuration
      );
      setAggregateRange("");
      getAggregateRanges(_resourceName);
    },
    [
      getAggregateRanges,
      setResourceName,
      setDashboardConfig,
      availableDashboards,
    ]
  );

  const updateSelectedAgg = React.useCallback((event) => {
    setAggregateRange(event.target.value);
  }, []);

  const presentationDashboardFilter = React.useCallback(
    () =>
      filters || (filter_by && filter_field)
        ? { [filter_field]: filter_by }
        : null,
    [filters, filter_by, filter_field]
  );

  // const memoizedPresentationDashboardFilter = React.useMemo(
  //   () => presentationDashboardFilter(filters, filter_by, filter_field),
  //   [presentationDashboardFilter, filters, filter_by, filter_field]
  // );
  
  return (hasData) ? (
    <React.Fragment>
      <div
        style={{ padding: "1em 0" }}
        className="presentation-dashboard"
        ref={componentRef}
      >
        {/* Print-only header */}
        {lastRunResults[aggregateRange] ? (
          <div
            className="show-on-print print-header"
            style={{ display: "flex", justifyContent: "space-between" }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" style={{ fontWeight: "bold" }}>
                {selectedReport} - {aggregateRange}
              </Typography>
              <Typography variant="subtitle1">
                <span style={{ fontWeight: "bold" }}>
                  {deploy_target.split("_").join(" ").toUpperCase()}:
                </span>
                &nbsp;{filter_by}
              </Typography>
              <Typography variant="subtitle1">
                <span style={{ fontWeight: "bold" }}>EOW:</span>&nbsp;
                {lastRunResults[aggregateRange].eow}
                {/* {commented out because it's different from the screen eow} */}
                {/* {new Date( 
                  lastRunResults[aggregateRange].eow
                ).toLocaleDateString()} */}
              </Typography>
            </div>

            <img
              src={clientContext.client.logo}
              alt={clientContext.client.name}
              style={{ maxHeight: 100 }}
            />
          </div>
        ) : null}

        {/* Dashboard options panel */}
        {loadingStatus !== "loading" && availableDashboards.length !== 0 && (
          <Paper
            className="hide-on-print"
            style={{
              padding: "1em",
              marginBottom: "1em",
            }}
          >
            <FormLabel>Dashboards</FormLabel>
            <section
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: ".5em",
              }}
            >
              {selectedReport && availableDashboards.length ? (
                <TextField
                  id="report-select"
                  style={{ paddingRight: "2em", width: 300 }}
                  select
                  margin="dense" 
                  value={selectedReport}
                  variant={"outlined"}
                  size="small"
                  onChange={updateSelectedReport}
                >
                  {availableDashboards.map((e, idx) => (
                    <MenuItem key={idx} value={e.title}>
                      {e.title}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              {selectedReport &&
              availableDashboards.length &&
              aggregateRanges.length ? (
                <TextField
                  id="range-select"
                  style={{ paddingRight: "2em", width: 140 }}
                  select
                  variant={"outlined"}
                  size={"small"}
                  margin="dense" 
                  value={aggregateRange}
                  defaultValue={aggregateRanges[0]}
                  onChange={updateSelectedAgg}
                  // helperText="Name of the resource containing the data for the report you'd like to dashboard."
                >
                  {aggregateRanges.map((e, idx) => (
                    <MenuItem key={idx} value={e}>
                      {e}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}

              {lastRunResults[aggregateRange] ? (
                <div style={{ opacity: 0.4 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    EOW: {lastRunResults[aggregateRange].eow}
                  </Typography>
                  <Typography variant="subtitle2" gutterBottom>
                    Last Updated: {moment(new Date(lastRunResults[aggregateRange].last_updated)).fromNow()}
                  </Typography>
                </div>
              ) : null}

              <div
                style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}
              >
                {availableDashboards.filter((e) => e.title === selectedReport)
                  .length >= 1 && aggregateRange ? (
                  <div style={{ margin: 5 }}>
                    <ReportDownloadButton
                      resource={resourceName}
                      label={`${aggregateRange} only`}
                      sort={{ field: "aggregate_range" }}
                      filter={{
                        "aggregate_range@eq": aggregateRange,
                        ...presentationDashboardFilter(),
                      }}
                    />
                  </div>
                ) : null}
                {availableDashboards.filter((e) => e.title === selectedReport)
                  .length >= 1 && aggregateRange ? (
                  <div style={{ margin: 5 }}>
                    <ReportDownloadButton
                      resource={resourceName}
                      sort={{ field: "aggregate_range" }}
                      label={"All Ranges"}
                      filter={{ ...presentationDashboardFilter() }}
                    />
                  </div>
                ) : null}
              </div>

              {availableDashboards.filter((e) => e.title === selectedReport && e.is_printable)
                .length >= 1 && aggregateRange ? (
                <PrintFrame content={() => componentRef.current} />
              ) : null}
            </section>
          </Paper>
        )}
        {loadingStatus && loadingStatus === "loading" ? (
          <LinearProgress variant="query" />
        ) : null}

        {/* Selected dashboard */}
        <Dashboard
          resourceName={resourceName}
          aggPeriod={aggregateRange}
          layouts={dashboardConfig} //defaultDashboardLayout
          isFixedLayout={true}
          onChange={(e) => e}
          filters={presentationDashboardFilter()}
        />
      </div>
    </React.Fragment>
  ) : 'There are no dashboards for this target';
};

export default PresentationDashboard;
